import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { Section, ToneChip, Meter, fmtN, fmtL, fmtP, num } from "./BpKit";
import { MonthGrid } from "./MonthGrid";
import { dshort } from "./WeeklyPo";
import { LucyAvatar } from "./Lucy";

const toneOf = (p) => (p == null ? null : p >= 95 ? "on_track" : p >= 80 ? "at_risk" : "behind");
const pctOf = (a, t) => (t ? Math.round((100 * a) / t) : null);

const Tile = ({ k, label, c, money }) => {
  const p = pctOf(c.actual, c.target);
  return (
    <div className="bg-white border border-slate-300 rounded-sm px-4 py-3 space-y-2" data-testid={`bp-win-team-${k}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">{label}</div>
        <ToneChip t={toneOf(p)} tid={`bp-win-team-${k}-tone`} />
      </div>
      <div className="num text-xl font-bold">{money ? fmtL(c.actual) : fmtN(c.actual)}<span className="text-sm font-normal text-slate-500"> / {c.target == null ? "no target" : money ? fmtL(c.target) : fmtN(c.target)}</span></div>
      <Meter pct={p} tone={toneOf(p)} tid={`bp-win-team-${k}-meter`} />
      {c.team_target && <div className="text-[10px] text-slate-500">Team target</div>}
    </div>
  );
};

const InvoiceCell = ({ r, week, fy, onSaved }) => {
  const [v, setV] = useState(r.actual.invoicing || "");
  useEffect(() => { setV(r.actual.invoicing || ""); }, [r.actual.invoicing]);
  const save = () => {
    if (num(v) === num(r.actual.invoicing)) return;
    api.put("/bp/invoicing", { user_id: r.user_id, week, value: num(v) }, { params: { fy } }).then(() => { toast.success(`Invoicing saved for ${r.name}`); onSaved(); }).catch((e) => toast.error(errMsg(e)));
  };
  return <input data-testid={`bp-win-invoice-${r.user_id}`} type="number" min="0" step="any" value={v} onChange={(e) => setV(e.target.value)} onBlur={save}
    onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} className="num w-[72px] h-8 px-1.5 text-right text-[13px] bg-white border border-slate-200 rounded-sm focus:outline-none focus:border-primary" />;
};

const Cell = ({ a, t, money }) => (
  <span className="num text-xs whitespace-nowrap"><b>{money ? fmtN(a) : a}</b><span className="text-slate-400"> / {t == null ? "—" : fmtN(t)}</span></span>
);

const People = ({ d, fy, onSaved }) => (
  <Section tid="bp-win-people" title="By person" hint="Actual / weekly target. Invoicing is entered here by the person, their manager or Finance (₹ L).">
    <div className="overflow-x-auto">
      <table className="ax-table ax-freeze w-full" data-testid="bp-win-people-table">
        <thead><tr><th>Person</th>{Object.entries(d.acts).map(([k, l]) => <th key={k} className="text-right whitespace-nowrap">{l}</th>)}</tr></thead>
        <tbody>
          {d.people.map((r) => (
            <tr key={r.user_id} data-testid={`bp-win-person-${r.user_id}`}>
              <td className="font-medium whitespace-nowrap">{r.name}</td>
              {Object.keys(d.acts).map((k) => (
                <td key={k} className="text-right">
                  {k === "invoicing" && r.can_invoice ? <div className="flex items-center justify-end gap-1"><InvoiceCell r={r} week={d.week.key} fy={fy} onSaved={onSaved} /><span className="num text-[11px] text-slate-400">/ {r.target.invoicing == null ? "—" : fmtN(r.target.invoicing)}</span></div>
                    : <Cell a={r.actual[k]} t={r.target[k]} money={d.money.includes(k)} />}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </Section>
);

const Trend = ({ rows }) => {
  const max = Math.max(1, ...rows.map((r) => Math.max(r.orders, r.orders_target || 0)));
  return (
    <Section tid="bp-win-trend" title="Orders, last 6 weeks" hint="PO value booked each BP week against the team's weekly order target (₹ L).">
      <div className="space-y-2.5">
        {rows.map((r) => (
          <div key={r.key} className="grid grid-cols-[88px_1fr_auto] items-center gap-3 text-xs" data-testid={`bp-win-trend-${r.key}`}>
            <span className="text-slate-500 whitespace-nowrap">{r.key} · {dshort(r.start)}</span>
            <div className="relative h-3 bg-slate-100 rounded-sm">
              <div className="absolute inset-y-0 left-0 bg-primary rounded-sm transition-[width] duration-700" style={{ width: `${(100 * r.orders) / max}%` }} />
              {r.orders_target ? <div className="absolute inset-y-[-3px] w-0.5 bg-slate-900" style={{ left: `${(100 * r.orders_target) / max}%` }} title="Target" /> : null}
            </div>
            <span className="num whitespace-nowrap"><b>{fmtN(r.orders)}</b>{r.orders_target ? <span className="text-slate-400"> / {fmtN(r.orders_target)}</span> : null} · {r.meetings} mtg</span>
          </div>
        ))}
      </div>
    </Section>
  );
};

const Leaders = ({ d }) => (
  <Section tid="bp-win-leaders" title="Order conversion leaderboard" hint={`FY ${d.fy} so far: orders booked, RFQ value raised and orders as a share of RFQs. RFQ in hand is open Pre-POs today.`}>
    <div className="overflow-x-auto">
      <table className="ax-table w-full" data-testid="bp-win-leaders-table">
        <thead><tr><th>#</th><th>Person</th><th className="text-right">Orders</th><th className="text-right">RFQ value</th><th className="text-right">Conversion</th><th className="text-right">RFQ in hand</th></tr></thead>
        <tbody>
          {d.leaders.map((r, i) => {
            const h = d.in_hand.find((x) => x.name === r.name);
            return (
              <tr key={r.user_id} data-testid={`bp-win-leader-${r.user_id}`}>
                <td className="num text-xs text-slate-500">{i + 1}</td><td className="font-medium whitespace-nowrap">{r.name}</td>
                <td className="text-right num font-semibold">{fmtL(r.orders)}</td><td className="text-right num">{fmtL(r.rfq_value)}</td>
                <td className="text-right num">{fmtP(r.conversion)}</td><td className="text-right num text-xs whitespace-nowrap">{h ? `${h.count} · ${fmtL(h.value)}` : "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </Section>
);

const TeamTargets = ({ meta }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) api.get("/bp/team-targets", { params: { fy: meta.fy } }).then((r) => setData(r.data.data)).catch((e) => toast.error(errMsg(e))); }, [open, meta.fy]);
  const rows = Object.entries(meta.acts).map(([k, l]) => ({ key: k, label: `${l} per week`, values: data?.[k] || {} }));
  const clean = () => Object.fromEntries(Object.entries(data).map(([k, r]) => [k, Object.fromEntries(Object.entries(r).filter(([, v]) => v !== "" && v != null).map(([m, v]) => [m, num(v)]))]));
  const save = () => { setBusy(true); api.put("/bp/team-targets", { data: clean() }, { params: { fy: meta.fy } }).then((r) => { setData(r.data.data); toast.success("Team targets saved"); }).catch((e) => toast.error(errMsg(e))).finally(() => setBusy(false)); };
  return (
    <Section tid="bp-win-team-targets" title="Team weekly targets" hint="Optional. When set, these replace the sum of people's targets in the team tiles above."
      right={<button type="button" data-testid="bp-win-team-targets-toggle" className="text-sm text-primary hover:underline" onClick={() => setOpen((o) => !o)}>{open ? "Hide" : "Edit team targets"}</button>}>
      {open && data && (
        <div className="space-y-3">
          <MonthGrid tid="bp-win-team-targets-grid" months={meta.months} rows={rows} onCell={(k, m, v) => setData((x) => ({ ...x, [k]: { ...(x[k] || {}), [m]: v } }))} />
          <button type="button" data-testid="bp-win-team-targets-save" disabled={busy} onClick={save} className="bg-primary text-primary-foreground h-9 px-4 text-sm rounded-sm font-medium disabled:opacity-50">Save team targets</button>
        </div>
      )}
    </Section>
  );
};

const Streaks = ({ rows }) => (
  <section className="border border-amber-300 bg-amber-50/60 rounded-sm p-4 space-y-2 ax-fade" data-testid="bp-win-streaks">
    <div className="flex items-center gap-2"><LucyAvatar size="h-7 w-7 text-xs" /><h3 className="text-sm font-semibold text-amber-900">Lucy's warning: meetings slipping two weeks running</h3></div>
    <ul className="space-y-1.5">
      {rows.map((s) => (
        <li key={s.user_id} className="text-sm text-amber-900 flex flex-wrap items-center gap-x-2" data-testid={`bp-win-streak-${s.user_id}`}>
          <span>{s.message}</span>
          <Link to={`/bp/week?fy=${s.fy || ""}&week=${s.weeks[1].key}&tab=meetings`} className="text-xs underline" data-testid={`bp-win-streak-link-${s.user_id}`}>See {s.weeks[1].key} meetings</Link>
        </li>
      ))}
    </ul>
  </section>
);

export const Winning = ({ meta, week, branch }) => {
  const [d, setD] = useState(null);
  const load = useCallback(() => {
    api.get("/bp/winning", { params: { fy: meta.fy, week, ...(branch && { branch }) } }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [meta.fy, week, branch]);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  return (
    <div className="space-y-6" data-testid="bp-win">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="bp-win-team">
        {Object.entries(d.acts).map(([k, l]) => <Tile key={k} k={k} label={l} c={d.team[k]} money={d.money.includes(k)} />)}
      </div>
      {d.streaks?.length > 0 && <Streaks rows={d.streaks.map((s) => ({ ...s, fy: d.fy }))} />}
      {d.people.length ? <People d={d} fy={meta.fy} onSaved={load} /> : <p className="text-sm text-slate-500" data-testid="bp-win-empty">No salespeople in this view.</p>}
      <div className="grid lg:grid-cols-2 gap-6"><Trend rows={d.trend} /><Leaders d={d} /></div>
      {d.can_team_targets && <TeamTargets meta={meta} />}
    </div>
  );
};
